import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { produce } from 'immer'
import { debounce } from 'lodash'
import { useFetchServiceLocations } from './useFetchServiceLocations'

export function useSearchServiceLocations(
  locationName: string,
  take: number = 5,
  branchIds?: string | string[],
) {
  const [state, setState] = useState(() => ({
    searchText: locationName,
    query: locationName,
    skip: 0,
    take,
  }))

  const searchInput = useMemo(
    () => ({
      query: state.query,
      skip: state.skip,
      take: state.take,
      branchIds,
    }),
    [branchIds, state.query, state.skip, state.take],
  )

  const data = useFetchServiceLocations(searchInput)

  const page = useMemo(
    () => Math.ceil((searchInput.skip ?? 0) / (searchInput.take ?? 1)) + 1,
    [searchInput.skip, searchInput.take],
  )

  const pages = useMemo(
    () => Math.ceil(data.count / data.take),
    [data.count, data.take],
  )

  const onChangeSearchText = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setState((state) =>
        produce(state, (draft) => {
          draft.searchText = event.target.value
        }),
      ),
    [],
  )

  const onChangePage = useCallback((_, newPage: number) => {
    setState((state) =>
      produce(state, (draft) => {
        draft.skip = (newPage - 1) * draft.take
        draft.query = draft.searchText
      }),
    )
  }, [])

  const debouncedUpdateQuery = useMemo(
    () =>
      debounce(() => {
        setState((state) =>
          produce(state, (draft) => {
            draft.query = draft.searchText.trim()
            draft.skip = 0
          }),
        )
      }, 250),
    [],
  )

  useEffect(
    () => void debouncedUpdateQuery(),
    [state.searchText, debouncedUpdateQuery],
  )

  return {
    ...data,
    page,
    pages,
    onChangePage,
    searchText: state.searchText,
    onChangeSearchText,
  }
}
